import {
  LinkIcon,
  MinusIcon,
  PaperClipIcon,
  PlusIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { Dialog } from '@headlessui/react';
import { PayoutDetailsWidget } from 'features/payments/PayoutDetailsWidget';
import { useMutation } from '@tanstack/react-query';
import { useCreateBillByVendorInvoice } from 'features/quickBooksOnline/useCreateBillByVendorInvoice';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import axios from 'axios';
import { dateFormatter, handleCopyInvoiceLink } from 'entities/bill/lib';
import { useBill, useBillActions, useBillMutations } from 'entities/bill/model';
import { useCurrentUserDetails, useCurrentUser } from 'entities/user/model';
import { BillApprovalButtons } from 'features/bill/ui';
import { forwardRef, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { DetailSection } from 'shared/components/DetailSection';
import { triggerFileDownload } from 'shared/lib';
import { Button, Modal } from 'shared/ui';
import { Button as UIKitButton } from 'shared/ui/Button/Button';
import { Spinner } from 'shared/ui/Spinner';
import { WithOrgPermissions } from 'shared/ui/WithOrgPermissions';
import FileUploadForm from './BillUploadFileForm';
import { useDialog } from 'shared/components/Dialog';
import { useFeatureFlags, features } from 'shared/hooks';
import { useAccountingData } from '../../../pages/bill/BillReconcilePage';
import {
  findSupplier,
  filterTaxRatesForBills,
  filterAccountsByLink,
  filterBankAccounts,
} from '../../../widgets/Bills/BillReconcileForm';
import { useAuth0 } from '@auth0/auth0-react';
import { Tooltip } from 'flowbite-react';
import { InformationCircleIcon } from '@heroicons/react/20/solid';
import { ROLES } from 'shared/hooks';
import { isCryptoCurrency } from 'features/bill/lib';

import { OnrampDepositDetailsSlideOverWidget } from '../../../features/bill/OnrampDepositDetails.SlideOverWidget';
import { PaymentMethodTypes } from '../../../features/payments/PaymentProviderIdTypes';

const Actions = forwardRef(({ id }, ref) => {
  const { data: bill } = useBill(id);
  const { data } = useCurrentUser();

  // const scrollDown = () => {
  //   ref.current.scrollIntoView({ behavior: 'smooth' });
  // };

  const approvalButtonsVisible =
    bill?._id &&
    bill?.approval?.approver === data.email &&
    bill?.approval?.status === 'pending';

  // const cryptoBill = bill?.network !== 'Fiat';

  return (
    <div className="flex gap-x-2">
      <WithOrgPermissions>
        {approvalButtonsVisible && <BillApprovalButtons id={bill._id} />}
        {/* {cryptoBill && !approvalButtonsVisible && (
          <Button onClick={scrollDown}>Pay</Button>
        )} */}
      </WithOrgPermissions>
    </div>
  );
});

export const BillDetails = forwardRef(
  ({ id, onReconciliationModalOpen, onPayBill, onClose }, ref) => {
    const dialog = useDialog();
    const { data: bill } = useBill(id);
    const { data } = useCurrentUserDetails();

    // const [selectApprover, toggleSelectApprover] = useToggle(false);

    // const [approver, setApprover] = useState(bill?.approval?.approver || '');
    const [showFileAttachmentForm, setShowFileAttachmentForm] = useState(false);

    const { downloadAction } = useBillActions();
    const { remove } = useBillMutations();

    const cryptoBill = bill?.network !== 'Fiat';

    // const handleAssign = useCallback(async () => {
    //   if (!approver) {
    //     return;
    //   }

    //   try {
    //     await update.mutateAsync({
    //       billId: bill?._id,
    //       params: {
    //         requester: data.email,
    //         requestedAt: new Date(),
    //         approver: approver,
    //         approvalRequired: true,
    //         organization: data?.selected_organization?._id,
    //       },
    //     });

    //     toast.success('New Approver Set!');
    //   } catch (error) {
    //     console.error(error);
    //   }
    // }, [
    //   approver,
    //   bill?._id,
    //   data.email,
    //   data?.selected_organization?._id,
    //   update,
    // ]);

    const handleDownload = useCallback(
      async (billId, fileName, resource) => {
        try {
          const file = await downloadAction(billId, resource);

          const name = fileName.replace(/\s/g, '');
          triggerFileDownload(`${name}${billId}.pdf`, file);
        } catch (error) {
          console.error(error);
        }
      },
      [downloadAction]
    );

    const handleDelete = async () => {
      const confirmationText =
        'Are you sure you want to delete this bill? This action cannot be undone and you and your team members will not able to see this bill anymore.';

      dialog.confirm({
        title: 'Delete Bill',
        desc: confirmationText,
        onConfirm: async () => {
          try {
            await remove.mutateAsync(bill?._id);

            toast.success('Bill Deleted!');

            onClose();
          } catch (error) {
            console.error(error);
          }
        },
      });
    };

    const attachements = [
      bill?.file && {
        filename: 'Attached File',
        source: 'A file that was attached by the sender.',
        handleDownload: () =>
          handleDownload(bill?._id, `${bill?.receiverName}_File_ID_`),
      },
      {
        filename: 'Auto Generated Invoice',
        source: 'An auto-generated invoice.',
        previewLink: `/public/bills/invoices?id=${bill?._id}`,
        onCopyClick: (e) => handleCopyInvoiceLink(e, bill?._id),
        handleDownload: () =>
          handleDownload(
            bill?._id,
            `${bill?.receiverName}_Invoice_ID_`,
            'download-invoice'
          ),
      },
      bill?.user_attachement?.attachement && {
        filename: bill?.user_attachement?.name,
        source: `Uploaded by ${bill?.user_attachement?.uploader}`,
        handleDownload: () =>
          handleDownload(
            bill?._id,
            `${bill?.user_attachement.name}_File_ID_`,
            'download-user-attachment'
          ),
      },
    ].filter((attachement) => Boolean(attachement));

    // Renderers

    const renderOnrampDepositDetails = () => {
      if (bill?.payablePayment && isCryptoCurrency(bill?.token)) {
        return (
          <OnrampDepositDetailsSlideOverWidget
            onrampPaymentStatus={bill?.payablePayment?.status}
            onrampPaymentMethod={bill?.payablePayment?.method}
            paymentProviderResponse={
              bill?.payablePayment?.paymentProviderResponse
            }
          />
        );
      }

      if (
        bill?.paymentTransaction &&
        bill?.paymentTransaction?.type === 'payso_fiat_crypto' &&
        bill?.paymentStatus === 'processingPayment'
      ) {
        return (
          <OnrampDepositDetailsSlideOverWidget
            onrampPaymentStatus={'initiated'}
            onrampPaymentMethod={PaymentMethodTypes.Payso}
            paymentProviderResponse={bill?.paymentTransaction?.details}
          />
        );
      }

      return null;
    };

    return (
      <div className="relative mb-40 flex-1 space-y-6">
        <PayoutDetailsWidget
          recipientName={bill?.receiverName}
          recipientEmail={bill?.receiverEmail}
          destinationAmount={bill?.amount}
          destinationCurrency={bill?.token}
          paidBy={bill.paidBy}
          destinationBlockchainNetwork={cryptoBill ? bill?.network : undefined}
          destinationBankAccount={bill?.invoice?.paymentDetails?.bankAccount}
          destinationWalletAddress={
            cryptoBill ? bill?.walletAddress : undefined
          }
          onPayButtonPress={() => {
            onPayBill(bill);
            onClose();
          }}
          paymentStatus={
            bill.paymentStatus === 'processingPayment'
              ? 'pending'
              : bill.paymentStatus
          }
          paymentBlockchainTxHash={bill.transactionHash}
        />

        <DetailSection header="Other Info">
          <DetailSection.Item term="Authentication">
            <span className="flex flex-col items-end">
              {bill?.issuedByUserId ? (
                <span className=" flex items-center  ">
                  <ShieldCheckIcon className="text-emerald-600 mr-1 inline w-4" />
                  Verified Email
                </span>
              ) : (
                <span className="flex items-center ">
                  <ShieldExclamationIcon className="text-amber-300  mr-1 inline w-4" />
                  Email Not Verified
                  <Tooltip
                    content={
                      <div className="w-[200px] text-slate-200 text-left">
                        This user sent a payment request from unverified email.
                        Please make sure you are paying to the right person.
                      </div>
                    }>
                    <InformationCircleIcon className="ml-1 w-4 cursor-pointer" />
                  </Tooltip>
                </span>
              )}
            </span>
          </DetailSection.Item>

          {bill.invoice?.referenceId && (
            <DetailSection.Item term="Invoice ID">
              {bill.invoice?.referenceId.slice(0, 46)}
            </DetailSection.Item>
          )}

          <DetailSection.Item term="Date">
            {dateFormatter(bill?.dueDate)}
          </DetailSection.Item>

          <DetailSection.Item term="Source">
            <LinkIcon className="mx-1 inline h-4 w-4" aria-hidden />
            {bill?.urlName || 'N/A'}
          </DetailSection.Item>
        </DetailSection>
        {renderOnrampDepositDetails()}

        <DetailSection header="Attachments">
          <dd className=" text-sm text-white sm:col-span-2 sm:mt-0">
            <ul
              role="list"
              className="divide-y divide-white/10 rounded-md  border-white/20">
              {attachements.length ? (
                attachements.map((attachement) => (
                  <AttachmentItem
                    key={attachement.filename}
                    attachement={attachement}
                  />
                ))
              ) : (
                <li className="flex items-center justify-between py-2 text-sm leading-6">
                  <div className="flex w-0 flex-1 items-center">
                    <PaperClipIcon
                      className="h-5 w-5 flex-shrink-0 text-gray-400"
                      aria-hidden="true"
                    />
                    <div className="ml-2 flex min-w-0 flex-1 text-gray-400 ">
                      <span className="truncate font-medium">
                        This bill has no attachments
                      </span>
                    </div>
                  </div>
                </li>
              )}
            </ul>
          </dd>
          <div className="py-3">
            <button
              type="button"
              className="group flex items-center rounded-md hover:border-indigo-500 focus:outline-none"
              onClick={() => setShowFileAttachmentForm((p) => !p)}>
              <span className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-dashed border-gray-300 text-gray-300 group-hover:border-indigo-500 group-hover:text-indigo-500">
                {showFileAttachmentForm ? (
                  <MinusIcon className="h-3 w-3" aria-hidden="true" />
                ) : (
                  <PlusIcon className="h-3 w-3" aria-hidden="true" />
                )}
              </span>
              <span className="ml-2 mr-2 text-sm font-medium text-white group-hover:text-indigo-500">
                {showFileAttachmentForm ? 'Remove File' : 'Attach File'}
              </span>
            </button>
            {showFileAttachmentForm && (
              <div className="py-3">
                <FileUploadForm
                  setShowForm={setShowFileAttachmentForm}
                  bill={bill}
                />
              </div>
            )}
          </div>
        </DetailSection>
        {/* <DetailSection header="Approval"> */}
        {/* <DetailSection.Item term="Status">
          <span className="flex items-center ">
            {bill?.approval?.status === 'approved' ? (
              <span className="flex items-center justify-end text-green-500">
                <CheckCircleIcon className="mr-1 inline h-4 w-4" />
                Approved
              </span>
            ) : bill?.approval?.status === 'rejected' ? (
              <span className="flex items-center justify-end text-red-500">
                <ExclamationTriangleIcon className="mr-1 inline h-4 w-4" />
                Rejected
              </span>
            ) : bill?.approval?.required ? (
              <span className="flex items-center justify-end text-yellow-500">
                <ClockIcon className="mr-1 inline h-4 w-4" /> Pending
              </span>
            ) : (
              'Not Required'
            )}
          </span>
        </DetailSection.Item> */}

        {/* <DetailSection.Item term="Approver">
            <span className="flex items-center ">
              <UserCircleIcon className="mr-1 h-4 w-4" />
              {bill?.approval?.approver ? bill?.approval.approver : 'N/A'}
            </span>
          </DetailSection.Item> */}

        {/* <DetailSection.Item term="Set Approver">
            <div className=" flex justify-end">
              <button
                className="font-medium text-indigo-400 hover:text-indigo-300"
                onClick={toggleSelectApprover}>
                Select New Approver
              </button>
            </div>
          </DetailSection.Item> */}

        {/* {selectApprover && (
            <div>
              <WithOrgPermissions>
                <select
                  id="approver"
                  name="approver"
                  type="select"
                  className="col-span-2 w-full rounded-md bg-[#18222d]  pl-3 pr-10 text-sm font-normal text-white placeholder-gray-600 shadow-sm focus:ring-primary-500"
                  value={approver}
                  onChange={(e) => setApprover(e.target.value)}>
                  <option value="">Select Approver</option>
                  {users.map((user) => (
                    <option key={user.email} value={user.email}>
                      {user.email}
                    </option>
                  ))}
                </select>
                <div className="mb-4 mt-4 flex justify-between">
                  <div className="space-x-4">
                    <Button onClick={handleAssign}>Assign</Button>
                    <Button ui="secondary" onClick={toggleSelectApprover}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </WithOrgPermissions>
            </div>
          )} */}

        {/* <WithOrgPermissions>
            <BillApprovalButtons id={id} />
          </WithOrgPermissions> */}
        {/* </DetailSection> */}

        <DetailSection header="Other Actions">
          {(() => {
            let statusContent = null;

            if (
              [ROLES.ADMIN, ROLES.ACCOUNTANT].includes(
                data?.selected_organization?.userRole
              ) &&
              data?.selected_organization?.integrations?.[0]?.codat
                ?.connectionId
            ) {
              statusContent = (
                <div className="flex justify-end">
                  <button
                    className="font-medium text-indigo-400 hover:text-indigo-300"
                    onClick={onReconciliationModalOpen}>
                    {bill?.accountingStatus?.reconciled
                      ? 'Re-reconcile'
                      : 'Reconcile Now'}
                  </button>
                </div>
              );
            }

            if (statusContent) {
              return (
                <div className="grid grid-cols-2 items-center gap-y-1 py-2 text-left text-sm font-medium">
                  <span className="text-slate-400">Reconcilliation</span>
                  {statusContent}
                </div>
              );
            }

            return null;
          })()}

          <WithOrgPermissions>
            <DetailSection.Item term="Delete Bill">
              <button
                onClick={handleDelete}
                type="button"
                className="font-medium text-rose-500  hover:text-rose-400">
                Delete Now
              </button>
            </DetailSection.Item>
          </WithOrgPermissions>
        </DetailSection>
      </div>
    );
  }
);

BillDetails.Actions = Actions;

const AttachmentItem = ({ attachement }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const _handleDownload = () => {
    setIsDownloading(true);
    attachement.handleDownload().finally(() => {
      setIsDownloading(false);
    });
  };

  return (
    <li className="flex items-center justify-between py-2  text-sm ">
      <div className="flex w-0 flex-1 items-center">
        <PaperClipIcon
          className="h-5 w-5 flex-shrink-0 text-slate-400"
          aria-hidden="true"
        />
        <div className="ml-2 flex min-w-0 flex-1 flex-col text-left ">
          <span className="truncate font-medium">{attachement.filename}</span>
          <span className="flex-shrink-0 text-xs text-slate-400">
            {attachement.source}
          </span>
        </div>
      </div>
      <div className="ml-4 flex-shrink-0">
        <UIKitButton
          outline
          color="transparentSlate"
          size="sm"
          disabled={isDownloading}
          isProcessing={isDownloading}
          onClick={_handleDownload}>
          <span className="text-indigo-400">Download</span>
        </UIKitButton>
      </div>
    </li>
  );
};

export const ReconciliationModal = ({ bill, modalOpened, handleClose }) => {
  const { updateBillAccountingCategory } = useUpdateAccountingCategory();
  const { isFeatureEnabled } = useFeatureFlags();
  const { invalidate: invalidateBills } = useBillMutations();

  const { mutateAsync: createBill } = useCreateBillByVendorInvoice();
  const { taxRates, zeroTaxRate, vendors, accounts } = useAccountingData();
  const { sortedSuppliers, top5Suppliers, remainingSuppliers } = findSupplier(
    vendors,
    bill
  );

  const filteredTaxRates = filterTaxRatesForBills(taxRates);
  const expenseAccounts = filterAccountsByLink(accounts);

  const bankAccounts = filterBankAccounts(accounts);

  const _allowTaxSelection = () => {
    return !!filteredTaxRates.length;
  };

  const _handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    try {
      await createBill({
        invoicePaymentRequestId: bill.paymentRequestId,
        qbVendorId: values.supplierId,
        qbAccountId: values.nominalAccountId,
        qbTaxAmount: values.lineItemTaxAmount,
      });
      toast.success('Successfully Reconciled Bill');
      invalidateBills();
      handleClose();
    } catch (error) {
      console.error(error);
      toast.error(
        'Was not able to reconcile a Bill, please contact support: pavel@getfractal.xyz'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // render

  const _renderSupplierHint = () => {
    return (
      <p className="mt-2 text-sm  text-[#CBD5E1]">
        We could not find the supplier for this bill. Please choose a supplier
        yourself or add a new supplier.
      </p>
    );
  };

  const _shouldShowPaymentAccounts = () => false;

  return (
    <Modal show={modalOpened} handleClose={handleClose}>
      <Dialog.Panel className="relative w-[620px]  transform overflow-hidden rounded-lg bg-gray-800 px-6 pb-6 pt-10 text-left text-white shadow-xl transition-all">
        <Dialog.Title
          as="h3"
          className="mb-8 text-center text-base font-semibold leading-6">
          Reconcile Bill
        </Dialog.Title>

        <Formik
          enableReinitialize
          onSubmit={_handleSubmit}
          initialValues={{
            supplierId: '',
            supplierName: '',
            newSupplier: false,
            newSupplierName: bill.receiverName,
            newSupplierEmail: bill.receiverEmail,
            nominalAccountId:
              bill?.accountingStatus?.selectedCategory?.id || '',
            nominalAccountName:
              bill?.accountingStatus?.selectedCategory?.name || '',
            paymentAccountId: '',
            paymentAccountName: '',
            attachTrxHash: isFeatureEnabled(features.RECONCILE_ATTACH_TRX_HASH),
            lineItemTaxAmount: 0,
            lineItemTaxId:
              Number(bill.tax) === 0 && zeroTaxRate ? zeroTaxRate.id : '',
            lineItemTaxName:
              Number(bill.tax) === 0 && zeroTaxRate ? zeroTaxRate.name : '',
          }}
          validate={(values) => {
            const errors = {};
            if (!values.supplierId && values.newSupplier === false) {
              errors.supplierId = 'Required';
            }
            if (!values.supplierName && values.newSupplier === false) {
              errors.supplierName = 'Required';
            }
            if (!values.newSupplierName && values.newSupplier === true) {
              errors.newSupplierName = 'Required';
            }
            if (!values.newSupplierEmail && values.newSupplier === true) {
              errors.newSupplierEmail = 'Valid email required';
            }
            if (!values.nominalAccountId) {
              errors.nominalAccountId = 'Required';
            }

            return errors;
          }}>
          {({ isSubmitting, values, setFieldValue }) => {
            return isSubmitting ? (
              <div className="mb-6 flex h-full w-full items-center justify-center">
                <Spinner />
              </div>
            ) : (
              <Form className="space-y-6">
                <div className="col-span-8 sm:col-span-6">
                  <label className="block text-sm font-medium text-gray-600 dark:text-[#CBD5E1]">
                    Choose Supplier *{' '}
                  </label>
                  <Field
                    as="select"
                    id="supplier"
                    name="supplier"
                    value={values.supplierId}
                    onChange={(e) => {
                      const supplier = sortedSuppliers.find(
                        (s) => e.target.value === s.Id
                      );

                      setFieldValue('supplierId', supplier.Id);
                      setFieldValue('supplierName', supplier.DisplayName);
                    }}
                    className="mt-1 block w-full rounded-md border-2 border-[#4E6180]   bg-[#18222d] text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ">
                    <option value="">Select Supplier</option>
                    <optgroup label="Top 5 Matches">
                      {top5Suppliers.map((supplier) => (
                        <option id={supplier.Id} value={supplier.Id}>
                          {supplier.DisplayName}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Other Suppliers">
                      {remainingSuppliers.map((supplier) => (
                        <option id={supplier.Id} value={supplier.Id}>
                          {supplier.DisplayName}
                        </option>
                      ))}
                    </optgroup>
                  </Field>
                  {_renderSupplierHint()}
                  <ErrorMessage
                    name="supplierId"
                    component="div"
                    className="mt-2 text-xs italic text-red-500"
                  />
                </div>
                {/* <div className="grid grid-cols-6 gap-6">
                      <div className="col-span-6 sm:col-span-3">
                        <Switch.Group as="div" className="items-left flex ">
                          <Switch.Label
                            as="span"
                            className="mr-5 text-sm font-medium leading-6 text-secondary-200"
                            passive>
                            Add New Supplier Manually
                          </Switch.Label>
                          <Switch
                            checked={values.newSupplier}
                            onChange={() => {
                              setFieldValue('newSupplier', !values.newSupplier);
                              setFieldValue('supplierId', '');
                              setFieldValue('supplierName', '');
                            }}
                            className={classNames(
                              values.newSupplier
                                ? 'bg-indigo-600'
                                : 'bg-gray-500',
                              'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2'
                            )}>
                            <span
                              aria-hidden="true"
                              className={classNames(
                                values.newSupplier
                                  ? 'translate-x-5'
                                  : 'translate-x-0',
                                'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                              )}
                            />
                          </Switch>
                        </Switch.Group>
                      </div>
                    </div> */}
                {values.newSupplier && (
                  <>
                    <div className="col-span-6">
                      <label className="block text-sm font-medium  text-[#CBD5E1]">
                        Supplier Name *{' '}
                      </label>
                      <Field
                        id="newSupplierName"
                        name="newSupplierName"
                        type="text"
                        value={values.newSupplierName}
                        onChange={(event) => {
                          setFieldValue('newSupplierName', event.target.value);
                        }}
                        className="mt-1 block w-full rounded-md border-2 border-[#4E6180]   bg-[#18222d] text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                      <p className="mt-2 text-sm  text-[#CBD5E1]">
                        Name of the supplier the bill was received from. Please
                        use a unique name.
                      </p>
                      <ErrorMessage
                        name="newSupplierName"
                        component="div"
                        className="mt-2 text-xs italic text-red-500"
                      />
                    </div>

                    <div className="col-span-6">
                      <label className="block text-sm font-medium  text-[#CBD5E1]">
                        Supplier Email *{' '}
                      </label>
                      <Field
                        id="newSupplierEmail"
                        name="newSupplierEmail"
                        type="text"
                        value={values.newSupplierEmail}
                        onChange={(event) => {
                          setFieldValue('newSupplierEmail', event.target.value);
                        }}
                        className="mt-1 block w-full rounded-md border-2 border-[#4E6180]   bg-[#18222d] text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                      <p className="mt-2 text-sm  text-[#CBD5E1]">
                        Name of the supplier the bill was received from. Email
                        must be valid.
                      </p>
                      <ErrorMessage
                        name="newSupplierEmail"
                        component="div"
                        className="mt-2 text-xs italic text-red-500"
                      />
                    </div>
                  </>
                )}

                <div className="col-span-6">
                  <label className="block text-sm font-medium  text-[#CBD5E1]">
                    Expense Category *
                  </label>
                  <Field
                    as="select"
                    type="select"
                    name="nominalAccount"
                    value={values.nominalAccountId}
                    onChange={(e) => {
                      const account = expenseAccounts.find(
                        (a) => a.Id === e.target.value
                      );
                      setFieldValue('nominalAccountId', account.Id);
                      setFieldValue('nominalAccountName', account.Name);

                      updateBillAccountingCategory({
                        billId: bill._id,
                        categoryId: account.Id,
                        categoryName: account.Name,
                      })
                        .then(() => {
                          invalidateBills();
                          toast.success('Saved Bill Expense Category!');
                        })
                        .catch((err) => {
                          console.log(err);
                        });
                    }}
                    className="mt-1 block w-full rounded-md border-2 border-[#4E6180]   bg-[#18222d] text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ">
                    <option value="">Please Select Account</option>
                    {expenseAccounts
                      .sort((a, b) => a.Name.localeCompare(b.Name))
                      .map((account) => (
                        <option key={account.Id} value={account.Id}>
                          {account.Name}
                        </option>
                      ))}
                  </Field>

                  <p className="mt-2 text-sm  text-[#CBD5E1]">
                    {values.nominalAccountId === ''
                      ? 'Select the nominal account to be used for the bill.'
                      : expenseAccounts.find(
                          (a) => a.Id === values.nominalAccountId
                        )?.description}
                  </p>
                  <ErrorMessage
                    name="nominalAccountId"
                    component="div"
                    className="mt-2 text-xs italic text-red-500"
                  />
                </div>

                {_allowTaxSelection() && (
                  <div className="col-span-6">
                    <label className="block text-sm font-medium  text-[#CBD5E1]">
                      Tax Rate *{' '}
                    </label>
                    <Field
                      as="select"
                      id="lineItemTaxName"
                      name="lineItemTaxName"
                      type="select"
                      value={values.lineItemTaxId}
                      onChange={(e) => {
                        const taxRate = filteredTaxRates.find(
                          (s) => e.target.value === s.Id
                        );

                        setFieldValue('lineItemTaxName', taxRate.Name);
                        setFieldValue('lineItemTaxAmount', taxRate.RateValue);
                        setFieldValue('lineItemTaxId', taxRate.Id);
                      }}
                      className="mt-1 block w-full rounded-md border-2 border-[#4E6180]   bg-[#18222d] text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ">
                      <option value="">Select Tax Rate</option>

                      {filteredTaxRates.map((taxRate) => {
                        return (
                          <option key={taxRate.Id} value={taxRate.Id}>
                            <span className="font-medium text-gray-900">
                              {'\u200BName: '}
                            </span>
                            {taxRate.Name}. <br />
                            <span className="font-medium text-gray-900">
                              {'\u200BTax Rate: '}
                            </span>
                            {taxRate.RateValue}%
                          </option>
                        );
                      })}
                    </Field>
                    <p className="mt-2 text-sm  text-[#CBD5E1]">
                      Amount of tax for the line.
                    </p>
                    <ErrorMessage
                      name="lineItemTaxName"
                      component="div"
                      className="mt-2 text-xs italic text-red-500"
                    />
                  </div>
                )}

                {_shouldShowPaymentAccounts() && (
                  <div className="col-span-6">
                    <label className="block text-sm font-medium  text-[#CBD5E1]">
                      Payment Account *
                    </label>
                    <Field
                      as="select"
                      type="select"
                      name="paymentAccount"
                      value={values.paymentAccountId}
                      onChange={(e) => {
                        const account = bankAccounts.find(
                          (a) => a.id === e.target.value
                        );
                        setFieldValue('paymentAccountId', account.id);
                        setFieldValue('paymentAccountName', account.name);
                      }}
                      className="mt-1 block w-full rounded-md border-2 border-[#4E6180]   bg-[#18222d] text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ">
                      <option value="">Please Select Payment Account</option>
                      {bankAccounts.map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.name}
                        </option>
                      ))}
                    </Field>

                    <p className="mt-2 text-sm  text-[#CBD5E1]">
                      {values.paymentAccountId === ''
                        ? 'Select the payment account to be used for the bill.'
                        : expenseAccounts.find(
                            (a) => a.id === values.paymentAccountId
                          )?.description}
                    </p>
                    <ErrorMessage
                      name="paymentAccountId"
                      component="div"
                      className="mt-2 text-xs italic text-red-500"
                    />
                  </div>
                )}

                <div className="!mt-12 flex flex-row-reverse gap-x-4">
                  <Button type="submit" className="flex-1 justify-center">
                    Reconcile Bill
                  </Button>
                  <Button
                    ui="secondary"
                    className="flex-1 justify-center"
                    onClick={handleClose}>
                    Cancel
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Dialog.Panel>
    </Modal>
  );
};

const useUpdateAccountingCategory = () => {
  const { getAccessTokenSilently } = useAuth0();
  const mut = useMutation({
    mutationKey: ['update-bill-accounting-category'],
    mutationFn: async ({ billId, categoryId, categoryName }) => {
      const token = await getAccessTokenSilently();
      const res = await axios.put(
        `${process.env.REACT_APP_SERVER_URL}/api/public/bills/${billId}/accounting-category`,
        {
          categoryId,
          categoryName,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      return res.data;
    },
  });

  return {
    updateBillAccountingCategory: mut.mutateAsync,
  };
};
